import { useFluent, useEventCallback } from '@fluentui/react-components';
import { getEventClientCoords, isMouseEvent, isTouchEvent } from '@fluentui/react-utilities';
import * as React from 'react';
import { EVENTS } from '../types';
export function useMouseHandler(params) {
  const {
    targetDocument,
    dir
  } = useFluent();
  const targetWindow = targetDocument == null ? void 0 : targetDocument.defaultView;
  const dragStartOriginCoords = React.useRef({
    clientX: 0,
    clientY: 0
  });
  const {
    growDirection,
    onValueChange,
    getCurrentValue
  } = params;
  const startValue = React.useRef(0);
  const recalculatePosition = useEventCallback(event => {
    const {
      clientX,
      clientY
    } = getEventClientCoords(event);
    const deltaCoords = [clientX - dragStartOriginCoords.current.clientX, clientY - dragStartOriginCoords.current.clientY];
    let newValue = startValue.current;
    switch (growDirection) {
      case 'end':
        newValue += deltaCoords[0] * (dir === 'rtl' ? -1 : 1);
        break;
      case 'start':
        newValue -= deltaCoords[0] * (dir === 'rtl' ? -1 : 1);
        break;
      case 'up':
        newValue -= deltaCoords[1];
        break;
      case 'down':
        newValue += deltaCoords[1];
        break;
    }
    onValueChange(event, {
      value: Math.round(newValue),
      ...(isTouchEvent(event) ? {
        event,
        type: EVENTS.touch
      } : {
        event,
        type: EVENTS.mouse
      })
    });
  });
  const onDrag = useEventCallback(event => {
    targetWindow == null ? void 0 : targetWindow.requestAnimationFrame(() => recalculatePosition(event));
  });
  const onDragEnd = useEventCallback(event => {
    if (isMouseEvent(event)) {
      targetDocument == null ? void 0 : targetDocument.removeEventListener('mouseup', onDragEnd);
      targetDocument == null ? void 0 : targetDocument.removeEventListener('mousemove', onDrag);
    }
    if (isTouchEvent(event)) {
      targetDocument == null ? void 0 : targetDocument.removeEventListener('touchend', onDragEnd);
      targetDocument == null ? void 0 : targetDocument.removeEventListener('touchmove', onDrag);
    }
    params.onDragEnd == null ? void 0 : params.onDragEnd.call(params, event, isTouchEvent(event) ? {
      event,
      type: EVENTS.touch
    } : {
      event,
      type: EVENTS.mouse
    });
  });
  const onPointerDown = useEventCallback(event => {
    dragStartOriginCoords.current = getEventClientCoords(event);
    // As we start dragging, save the current value otherwise the value increases,
    // the delta compounds and the element grows/shrinks too fast.
    startValue.current = getCurrentValue();
    if (event.defaultPrevented) {
      return;
    }
    if (isMouseEvent(event)) {
      // ignore other buttons than primary mouse button
      if (event.target !== event.currentTarget || event.button !== 0) {
        return;
      }
      targetDocument == null ? void 0 : targetDocument.addEventListener('mouseup', onDragEnd);
      targetDocument == null ? void 0 : targetDocument.addEventListener('mousemove', onDrag);
    }
    if (isTouchEvent(event)) {
      targetDocument == null ? void 0 : targetDocument.addEventListener('touchend', onDragEnd);
      targetDocument == null ? void 0 : targetDocument.addEventListener('touchmove', onDrag);
    }
    params.onDragStart == null ? void 0 : params.onDragStart.call(params, event, isTouchEvent(event) ? {
      event,
      type: EVENTS.touch
    } : {
      event,
      type: EVENTS.mouse
    });
  });
  const attachHandlers = React.useCallback(node => {
    node.addEventListener('mousedown', onPointerDown);
    node.addEventListener('touchstart', onPointerDown);
  }, [onPointerDown]);
  const detachHandlers = React.useCallback(node => {
    node.removeEventListener('mousedown', onPointerDown);
    node.removeEventListener('touchstart', onPointerDown);
  }, [onPointerDown]);
  return {
    attachHandlers,
    detachHandlers
  };
}